import React, { useEffect, useRef, useState } from "react";
import {
  FaSignOutAlt,
  FaFilter,
  FaSort,
  FaFileAlt,
  FaSignInAlt,
} from "react-icons/fa";
import {
  getAttendance,
  getAttendanceAPi,
  getAttendanceCards,
  getDashboardData,
  getLeaveBalances,
  getLeaveDetails,
  getUpcomingLeaves,
  postBreakIn,
  postBreakOut,
  postCheckIn,
  postCheckOut,
} from "../../ApiHelper/url_Helpers";
import dayjs from "dayjs";
import DynamicTabs from "../../Components/DynamicTabs";
import "./leaveSummary.css";
import ApplyLeaveModal from "./ApplyLeave";
import { Utensils, UtensilsCrossed, } from "lucide-react";
import { toasterSuccess } from "../../Components/toaster";
import CommonTable from "../../Components/CommonTable";
import Loader from "../../Components/Loader";
import { getWorkingDuration, mergeParamsIntoUrl, sortOptions1, useLiveTimer } from "../Dashboard/DashboardData";
import WarningModal from "../../Components/WarningModal";
import CheckInModal from "../../Components/CheckInModal";
import CheckOutModal from "../../Components/CheckOutModal";
import hourlyPunchService from "../../services/hourlyPunchService";
import liveTrackingService from "../../services/liveTrackingService";
import SortDropdown from "../../Components/SortDropDown";
import FilterDropdown from "./FilterDropDown";
import { useLocation, useNavigate } from "react-router-dom";


export const Attendance = () => {
  const fetchedRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
  const [faceVerificationRequired, setFaceVerificationRequired] = useState(false);
  const [isCheck, setIsCheck] = useState("");
  const [isBreak, setIsBreak] = useState("");
  const [isApply, setIsApply] = useState("");
  const [liveTimer, setLiveTimer] = useState("00:00:00");
  const [isAction, setIsAction] = useState();
  const [summary, setSummary] = useState();
  const [showPopup, setShowPopup] = useState(false);
  const [attendanceList, setAttendanceList] = useState(null);
  const [upcomingLeavesList, setUpcomingLeavesList] = useState(null);
  const [holidaysList, setHolidaysList] = useState(null);

  const [attendancePage, setAttendancePage] = useState(1);
  const [leavesPage, setLeavesPage] = useState(1);
  const livetimer = useLiveTimer(isAction?.check_in_time, isAction?.check_out_time);

  // Attendance filters/sort/date search
  const [attendanceFilters, setAttendanceFilters] = useState({
    year: "",
    month: "",
    status: "",
    work_location: "",
    date_from: "",
    date_to: "",
    ordering: "",
  });
  // Upcoming leaves filters/sort/date search
  const [leavesFilters, setLeavesFilters] = useState({
    year: "",
    month: "",
    status: "",
    leave_type: "",
    date_from: "",
    date_to: "",
    ordering: "",
  });

  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");

  useEffect(() => {
    setTempFromDate(attendanceFilters.date_from || "");
    setTempToDate(attendanceFilters.date_to || "");
  }, [attendanceFilters.date_from, attendanceFilters.date_to]);

  const buildParams = (filters) =>
    Object.fromEntries(
      Object.entries(filters)?.filter(([, v]) => v !== "" && v !== null && v !== undefined),
    );

  // Attendance Logs
  const fetchAttendance = async (url = null, paramsOverride = null) => {
    try {
      setLoading(true);
      const params = paramsOverride || buildParams(attendanceFilters);
      const resp = url
        ? await getAttendanceAPi(mergeParamsIntoUrl(url, params))
        : await getAttendance(params);
      fetchCards();
      fetchLeaveBalances();
      setAttendanceList(resp);
      setIsBreak(resp?.results?.action?.break === "on_break");
      setIsCheck(!!resp?.results?.action?.check_in);
      setIsAction(resp?.results?.action);
      setShowPopup(resp?.results?.action?.show_checkin_reminder);

      const isCheckedIn = Boolean(resp?.results?.action?.check_in && !resp?.results?.action?.check_out);
      if (isCheckedIn && hourlyPunchService.isEnabled()) {
        hourlyPunchService.start(resp?.results?.action?.check_in);
      } else {
        hourlyPunchService.stop();
      }

      if (isCheckedIn) {
        liveTrackingService.start();
      } else {
        liveTrackingService.stop();
      }

    } catch (err) {
      console.error("Attendance fetch error:", err);
    } finally {
      setLoading(false);
    }
  };
  // Upcoming Leaves
  const fetchUpcomingLeaves = async (url = null, paramsOverride = null) => {
    try {
      setLoading(true);
      const params = paramsOverride || buildParams(leavesFilters);
      const resp = url
        ? await getUpcomingLeaves(mergeParamsIntoUrl(url, params))
        : await getUpcomingLeaves(params);
        
      fetchCards();
      setUpcomingLeavesList(resp);
    } catch (err) {
      console.error("Leaves fetch error:", err);
    } finally {
      setLoading(false);
    }
  };
  // Leave Balance
  const fetchLeaveBalances = async () => {
    try {
      setLoading(true);
      const res = await getLeaveDetails();
      fetchCards();
      localStorage?.setItem("leaveType", JSON.stringify(res?.data?.leave_options || []));
      setHolidaysList(res?.data?.leave_summary || []);
    } finally {
      setLoading(false);
    }
  };
  const fetchCards = async () => {
    try {
      setLoading(true);
      const res = await getAttendanceCards();
      setSummary(res);
    } finally {
      setLoading(false);
    }
  };

  // Fetch face_verification_required & hourly_location_punch_enabled from dashboard API once on mount
  useEffect(() => {
    getDashboardData()
      .then((res) => {
        setFaceVerificationRequired(res?.face_verification_required ?? false);
        if (res?.profile?.hourly_location_punch_enabled !== undefined) {
          hourlyPunchService.setEnabled(res.profile.hourly_location_punch_enabled);
        }
      })
      .catch(() => {});
  }, []);

  const handleAttendancePageChange = (url) => {
    if (!url || !isLogsTab) return;
    const pageNo = Number(new URL(url).searchParams.get("page")) || 1;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", pageNo);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });

  };

  const handleLeavesPageChange = (url) => {
    if (!url || !isLeavesTab) return;
    const pageNo = Number(new URL(url).searchParams.get("page")) || 1;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", pageNo);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleBreak = async (leave = false) => {
    try {
      setLoading(true);
      let apiAction = leave ? postBreakIn : postBreakOut;
      const response = await apiAction();
      if (isLogsTab) fetchAttendance();
      else if (isLeavesTab) fetchUpcomingLeaves();
      else fetchLeaveBalances();
      toasterSuccess(response?.message);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const handleCheck = async (check = false) => {
    try {
      let apiAction = check ? postCheckIn : postCheckOut;
      setLoading(true);
      const response = await apiAction();
      if (isLogsTab) fetchAttendance();
      else if (isLeavesTab) fetchUpcomingLeaves();
      else fetchLeaveBalances();
      toasterSuccess(response?.message);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderAttendanceScreen = () => {
    return (
      <div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          {/* Date Selectors */}
          <div className="flex flex-wrap gap-2 md:gap-3 w-full sm:w-auto">
            <input
              type="date"
              value={tempFromDate}
              onChange={(e) => {
                setTempFromDate(e.target.value);
                if (!e.target.value) {
                  setTempToDate("");
                }
              }}
              className="flex-1 sm:flex-none border border-gray-300 rounded-md p-2 px-4 text-sm font-semibold bg-white outline-none focus:ring-2 focus:ring-cyan-500"
            />

            <input
              type="date"
              value={tempToDate}
              onChange={(e) => setTempToDate(e.target.value)}
              min={tempFromDate}
              disabled={!tempFromDate}
              className="flex-1 sm:flex-none border border-gray-300 rounded-md p-2 px-4 text-sm font-semibold bg-white outline-none focus:ring-2 focus:ring-cyan-500"
            />

            <button
              onClick={() => {
                setAttendanceFilters((s) => ({
                  ...s,
                  date_from: tempFromDate,
                  date_to: tempToDate,
                }));
              }}
              className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-md"
              disabled={
                !tempFromDate &&
                !tempToDate &&
                !attendanceFilters.date_from &&
                !attendanceFilters.date_to
              }
            >
              Go
            </button>
          </div>

          <div className="flex gap-2 md:gap-4 w-full sm:w-auto">
            <div className="relative group">
              <FilterDropdown
                value={attendanceFilters}
                onChange={(patch) =>
                  setAttendanceFilters((s) => ({ ...s, ...patch }))
                }
              />
            </div>
            <div className="relative group">
              <SortDropdown
                value={attendanceFilters?.ordering}
                onChange={(ordering) =>
                  setAttendanceFilters((s) => ({ ...s, ordering }))
                }
                options={sortOptions1}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setAttendanceFilters({
                  year: "",
                  month: "",
                  status: "",
                  work_location: "",
                  date_from: "",
                  date_to: "",
                  ordering: "",
                });
                setLeavesFilters({
                  year: "",
                  month: "",
                  status: "",
                  leave_type: "",
                  date_from: "",
                  date_to: "",
                  ordering: "",
                });
                setAttendancePage(1);
                setLeavesPage(1);
                navigate(location.pathname, { replace: true });
              }}
              className="flex-1 sm:flex-none border border-gray-300 rounded-md py-2 px-4 text-sm font-semibold text-gray-500 hover:bg-gray-100"
            >
              Reset All
            </button>
          </div>
        </div>
        {/* Attendance Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
          <CommonTable
            data={attendanceList?.results?.attendance_logs || []}
            rowKey="date"
            columns={[
              {
                key: "date", label: "Date",
                render: (v) => dayjs(v).format("DD-MM-YYYY"),
              },
              {
                key: "check_in", label: "Check-in",
                render: (v, row) => (
                  <span>
                    {row?.check_in
                      ? dayjs(row?.check_in).format("hh:mm A")
                      : "-"}{" "}
                  </span>
                ),
              },
              {
                key: "check_out", label: "Check-out",
                render: (v, row) => (
                  <span>
                    {row?.check_out
                      ? dayjs(row?.check_out).format("hh:mm A")
                      : "-"}
                  </span>
                ),
              },
              {
                key: "working_duration", label: "Work-Duration",
                render: (v) => {
                  if (!v || v === 0 || v === "0") {
                    return "00 : 00 ";
                  }
                  return `${v}`;
                }
              },
              {
                key: "break_info", label: "Break-Duration",
                render: (v) => {
                  if (!v || v === "No Break" || v === 0 || v === "0") {
                    return "0 minutes";
                  }
                  return v;
                },
              },
              {
                key: "status", label: "Status",
                render: (v) => (
                  <span
                    className={
                      v === "present" ? "text-green-500" : v === "absent" ? "text-red-500"
                        : v === "weekend" ? "text-yellow-500" : "text-gray-500"}
                  > {v}
                  </span>
                ),
              },
              { key: "work_location", label: "Work Location" },
              { key: "remarks", label: "Remarks" },
            ]}
            pagination={true}
            searchable={true}
            expandedRowRender={(row) => (
              <div className="space-y-2">
                {row.break_sessions?.map((b, i) => (
                  <div
                    key={i}
                    className="flex justify-between text-sm text-gray-500"
                  >
                    <span>Break Time {i + 1}</span>
                    <span>
                      {dayjs(b.start).format("hh:mm A")} -{" "}
                      {b.end ? dayjs(b.end).format("hh:mm A") : "00 :00 "}
                    </span>
                    <span>{b.duration || "00 : 00 "}</span>
                  </div>
                ))}
                <div className="flex justify-end text-sm text-gray-500">
                  <span>{row.break_info}</span>
                </div>
              </div>
            )}
            nextPageUrl={attendanceList?.next}
            prevPageUrl={attendanceList?.previous}
            onPageChange={handleAttendancePageChange}
            totalCount={attendanceList?.count || 0}
            page={attendancePage}
          />
        </div>
      </div>
    );
  };

  const renderUpComingLeaves = () => {
    return (
      <div>
        {/* Attendance Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm mt-5">
          <CommonTable
            data={upcomingLeavesList?.results?.leaves || []}
            rowKey="start_date"
            columns={[
              { key: "leave_type", label: "Leave Type" },
              {
                key: "start_date", label: "Start Date",
                render: (v) => dayjs(v).format("DD-MM-YYYY"),
              },
              {
                key: "end_date", label: "End Date",
                render: (v) => dayjs(v).format("DD-MM-YYYY"),
              },
              { key: "days", label: "No.of Days" },
              {
                key: "status", label: "Status",
                render: (v) => (
                  <span
                    className={v === "approved" ? "text-green-500" : v === "rejected" ? "text-red-500"
                      : v === "pending" ? "text-yellow-500" : "text-gray-500"}
                  > {v} </span>
                ),
              },
            ]}
            pagination={true}
            searchable={false}
            nextPageUrl={upcomingLeavesList?.next}
            prevPageUrl={upcomingLeavesList?.previous}
            onPageChange={handleLeavesPageChange}
            totalCount={upcomingLeavesList?.count || 0}
            page={leavesPage}
          />
        </div>
      </div>
    );
  };

  const renderLeaveBalance = () => {
    return (
      <div className=" mt-5">
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {holidaysList && holidaysList.length > 0 ? (
            holidaysList.map((item) => {
              const usedPercent = Math.round(
                (item.used / item.allocated) * 100
              );

              return (
                <div
                  key={item?.code || item.leave_code}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-800 capitalize">
                      {item?.name || item.leave_name}
                    </h3>
                    <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full font-medium">
                      {item?.remaining || item.left} Left
                    </span>
                  </div>

                  {/* Numbers */}
                  <div className="flex justify-between text-sm text-gray-600 mb-3">
                    <span>
                      <b className="text-gray-900">{item.allocated}</b> Allocated
                    </span>
                    <span>
                      <b className="text-gray-900">{item.used}</b> Used
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 bg-cyan-500 rounded-full transition-all"
                      style={{ width: `${usedPercent}%` }}
                    />
                  </div>

                  {/* Footer */}
                  <p className="mt-2 text-xs text-gray-500">
                    {usedPercent}% of leaves used
                  </p>
                </div>
              );
            })
          ) : (
            <div className="col-span-1 md:col-span-2 text-center text-gray-500 py-8 font-medium">
              Leave Balance Is Not Added
            </div>
          )}
        </div>
      </div>
    );
  };

  const tabs = [
    {
      id: "1",
      label: "Attendance Logs",
      path: "/attendance/logs",
      component: renderAttendanceScreen(),
    },
    {
      id: "2",
      label: "Leaves History",
      path: "/attendance/leaves",
      component: renderUpComingLeaves(),
    },
    {
      id: "3",
      label: "Leave Balance",
      path: "/attendance/balance",
      component: renderLeaveBalance(),
    },
  ];

  const isLogsTab = location.pathname.endsWith("/logs");
  const isLeavesTab = location.pathname.endsWith("/leaves");

  const activeTabId = isLogsTab ? "1" : isLeavesTab ? "2" : location.pathname.endsWith("/balance")
    ? "3" : "1";
  const hasAnyFilter = (filters) =>
    Object.values(filters).some((v) => v !== "" && v !== null && v !== undefined);

  const handleApi = () => {
    if (isLogsTab) fetchAttendance();
    else if (isLeavesTab) fetchUpcomingLeaves();
    else fetchLeaveBalances();
  }

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const params = new URLSearchParams(location.search);
    if (isLogsTab) {
      setAttendanceFilters((s) => ({
        ...s,
        status: params.get("status") || "",
        work_location: params.get("work_location") || "",
        ordering: params.get("ordering") || "",
        year: params.get("year") || "",
        month: params.get("month") || "",
        date_from: params.get("date_from") || "",
        date_to: params.get("date_to") || "",
      }));
      setAttendancePage(Number(params.get("page")) || 1);
    } else if (isLeavesTab) {
      setLeavesFilters((s) => ({
        ...s,
        status: params.get("status") || "",
        leave_type: params.get("leave_type") || "",
        year: params.get("year") || "",
        month: params.get("month") || "",
        date_from: params.get("date_from") || "",
        date_to: params.get("date_to") || "",
        ordering: params.get("ordering") || "",
      }));
      setLeavesPage(Number(params.get("page")) || 1);
    }
  }, []);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === "/attendance") {
      navigate("/attendance/logs", { replace: true });
    }

    if (isLogsTab) {
      const pageNo = Number(params.get("page")) || 1;
      setAttendancePage(pageNo);

      const nextFilters = {
        status: params.get("status") || "",
        work_location: params.get("work_location") || "",
        ordering: params.get("ordering") || "",
        year: params.get("year") || "",
        month: params.get("month") || "",
        date_from: params.get("date_from") || "",
        date_to: params.get("date_to") || "",
      };
      setAttendanceFilters(nextFilters);

      const base = `${process.env.REACT_APP_API_BASE_URL}attendance/api/employee/Attendance-Logs/`;
      const url = pageNo > 1 ? `${base}?page=${pageNo}` : null;
      fetchAttendance(url, buildParams(nextFilters));
    } else if (isLeavesTab) {
      const pageNo = Number(params.get("page")) || 1;
      setLeavesPage(pageNo);

      const nextFilters = {
        status: params.get("status") || "",
        leave_type: params.get("leave_type") || "",
        year: params.get("year") || "",
        month: params.get("month") || "",
        date_from: params.get("date_from") || "",
        date_to: params.get("date_to") || "",
        ordering: params.get("ordering") || "",
      };
      setLeavesFilters(nextFilters);

      const base = `${process.env.REACT_APP_API_BASE_URL}attendance/api/employee/Upcoming-Leaves/`;
      const url = pageNo > 1 ? `${base}?page=${pageNo}` : null;
      fetchUpcomingLeaves(url, buildParams(nextFilters));
    } else {
      fetchLeaveBalances();
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!fetchedRef.current) return;
    const searchParams = new URLSearchParams();

    if (isLogsTab) {
      Object.entries(attendanceFilters).forEach(([key, value]) => {
        if (value) searchParams.set(key, value);
      });
      if (attendancePage > 1) searchParams.set("page", attendancePage);
    } else if (isLeavesTab) {
      Object.entries(leavesFilters).forEach(([key, value]) => {
        if (value) searchParams.set(key, value);
      });
      if (leavesPage > 1) searchParams.set("page", leavesPage);
    }

    const currentParams = new URLSearchParams(location.search);
    if (searchParams.toString() !== currentParams.toString()) {
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }
  }, [attendanceFilters, leavesFilters, attendancePage, leavesPage]);

  return (
    <div>
      {showPopup && (
        <WarningModal
          open={showPopup}
          title="Check-in Required"
          message="You have not checked in yet."
          subMessage="Please check in to start your workday."
          confirmText="Check In Now"
          cancelText="Later"
          onCancel={() => setShowPopup(false)}
          onConfirm={() => {
            setShowPopup(false);
            setIsCheckInModalOpen(true);
          }}
        />
      )}
      <div className=" md:p-4  p-4 md:p-8 bg-[#f7f9fc] min-h-screen">
        <div className=" mx-auto   rounded-lg ">
          {/* Header Section */}
          <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <span className="text-xl  font-semibold  text-gray-800">
                Attendance
              </span>
              <p className="text-sm   text-gray-500">
                Your daily attendance overview
              </p>
            </div>
            <div className="flex  justify-between ">
              <button
                className="w-full sm:w-auto bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-2 px-4 me-2 rounded-md shadow-md flex items-center justify-center space-x-2 transition duration-150"
                onClick={() =>
                  !isBreak && !isAction?.check_out_time ? handleBreak(true) : handleBreak(false)
                }
                disabled={isAction?.check_out_time}
              >
                {!isBreak ? (
                  <Utensils className="w-4 h-4" />
                ) : (
                  <UtensilsCrossed className="w-4 h-4" />
                )}
                <span className="text-sm">
                  {isBreak ? "Break End" : "Break Start "}
                </span>
              </button>
              <button
                className="w-full sm:w-auto bg-cyan-500 hover:bg-cyan-600 text-white ms-2 font-medium py-2 px-4 rounded-md shadow-md flex items-center justify-center space-x-2 transition duration-150"
                onClick={() => (isCheck && !isAction?.check_out_time ? setIsCheckOutModalOpen(true) : setIsCheckInModalOpen(true))}
                disabled={isAction?.check_out_time}
              >
                {isCheck ? (
                  <FaSignOutAlt className="w-4 h-4" />
                ) : (
                  <FaSignInAlt className="w-4 h-4" />
                )}
                <span className="text-sm">
                  {isCheck ? "Check Out" : "Check In"}
                </span>
              </button>
            </div>
          </header>
          {/* Stats Cards Section */}
          {loading ? (
            <Loader />
          ) : (
            <div>
              <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                {/* Total Days */}
                <div className="p-4 border border-gray-200 rounded-lg bg-white">
                  <p className="text-sm font-semibold text-gray-500">
                    Total Days
                  </p>
                  <p className="text-md md:text-lg font-bold text-gray-700 mt-1">
                    {summary?.total_days || 0}
                  </p>
                </div>
                {/* Present Days */}
                <div className="p-4 border border-gray-200 rounded-lg bg-white">
                  <p className="text-sm font-semibold text-gray-500">
                    Working Days
                  </p>
                  <p className="text-md md:text-lg font-bold text-gray-700 mt-1">
                    {summary?.working_days || 0}
                  </p>
                </div>
                {/* Absent Days */}
                <div className="p-4 border border-gray-200 rounded-lg bg-white">
                  <p className="text-sm font-semibold text-gray-500">Leaves </p>
                  <p className="text-md md:text-lg font-bold text-gray-700 mt-1">
                    {summary?.leaves || 0}
                  </p>
                </div>
                {/* Working Hours */}
                <div className="p-4 border border-gray-200 rounded-lg bg-white">
                  <p className="text-sm font-semibold text-gray-500">
                    Today Working Hours
                  </p>
                  <p className="text-md md:text-lg font-bold text-gray-700 mt-1">
                    {attendanceList?.results?.action?.check_out
                      ? summary?.today_working_duration
                      : livetimer || "00:00:00"}
                  </p>
                </div>
                {/* Apply Leave Action Card */}
                <div
                  className="p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-100 flex items-center justify-star cursor-pointer transition duration-150 sm:col-span-2 lg:col-span-1"
                  onClick={() => setOpen(true)}
                >
                  <div className="text-gray-500 text-start">
                    <FaFileAlt className="w-5 h-5 md:w-6 md:h-6 mb-1" />
                    <span className="font-semibold text-sm text-gray-500">
                      Apply Leave
                    </span>
                  </div>
                </div>
              </section>
              <section className="mb-8">
                <DynamicTabs tabs={tabs} activeTab={activeTabId} onTabChange={(nextTab) => { navigate(nextTab.path);; }} />
              </section>
            </div>
          )}
          {open && (
            <ApplyLeaveModal
              open={open}
              onClose={() => { setOpen(false); handleApi(); }}
              setIsApply={setIsApply}
            />
          )}
          {isCheckInModalOpen && (
            <CheckInModal
              isOpen={isCheckInModalOpen}
              onClose={() => setIsCheckInModalOpen(false)}
              onSuccess={() => handleApi()}
              defaultLocationName={
                attendanceList?.results?.action?.work_location || ""
              }
              defaultWorkMode={attendanceList?.results?.action?.work_mode || "office"}
              faceVerificationRequired={faceVerificationRequired}
            />
          )}
          {isCheckOutModalOpen && (
            <CheckOutModal
              isOpen={isCheckOutModalOpen}
              onClose={() => setIsCheckOutModalOpen(false)}
              onSuccess={() => handleApi()}
              defaultLocationName={
                attendanceList?.results?.action?.work_location || ""
              }
              defaultWorkMode={attendanceList?.results?.action?.work_mode || "office"}
              faceVerificationRequired={faceVerificationRequired}
            />
          )}
        </div>
      </div>
    </div>
  );
};
